import { useState, useSyncExternalStore } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MaskotElvis } from './MaskotElvis';
import { useElvisKort } from '@/hooks/useElvisKort';

const FIGURER = {
  elvis: { namn: 'ELvis' },
  elvira: { namn: 'Elvira', bild: '/maskot/elvira.webp' },
} as const;

/** Tailwinds xl – samma brytpunkt som MASKOT_RANNA i lib/maskot.ts. */
const XL = '(min-width: 1280px)';

function useXl(): boolean {
  return useSyncExternalStore(
    (andrad) => {
      const mq = window.matchMedia(XL);
      mq.addEventListener('change', andrad);
      return () => mq.removeEventListener('change', andrad);
    },
    () => window.matchMedia(XL).matches,
    () => false,
  );
}

const DOLD_NYCKEL = 'elvis-maskot-dold';

function lasDold(vem: string): boolean {
  try {
    return sessionStorage.getItem(`${DOLD_NYCKEL}-${vem}`) === '1';
  } catch {
    return false;
  }
}

/**
 * Flikens maskot i nedre högra hörnet. Svävar lugnt; vid hover studsar den
 * till och berättar vad den kan hjälpa till med.
 *
 * Renderas bara på breda skärmar – under xl finns ingen ränna att stå i, och
 * då ska den inte heller ladda bilder eller lyssna på tangenter. Står still
 * vid prefers-reduced-motion (index.css). "Dölj" gäller besöket
 * (sessionStorage), så att ett felklick inte tar bort den för gott.
 */
export function Maskot({ vem, bubbla }: { vem: keyof typeof FIGURER; bubbla: string }) {
  const xl = useXl();
  const [dold, setDold] = useState(() => lasDold(vem));
  // ELvis bubbla byter kort vid varje hover: tips eller "Visste du?" med riktiga siffror
  const elvisKort = useElvisKort();
  const figur = FIGURER[vem];
  if (!xl || dold) return null;
  const arElvis = vem === 'elvis';

  const dolj = () => {
    setDold(true);
    try {
      sessionStorage.setItem(`${DOLD_NYCKEL}-${vem}`, '1');
    } catch {
      /* privat läge: dold tills sidan laddas om */
    }
  };

  return (
    <div
      className="maskot group fixed bottom-5 right-6 z-20 w-[112px] select-none"
      // Bara när muspekaren kommer in – fokus som bubblar upp från stängknappen
      // hade bytt kort medan bubblan redan visas
      onMouseEnter={arElvis ? elvisKort.nasta : undefined}
    >
      {/* Pratbubblan till vänster om figuren: syns vid hover och tangentbordsfokus */}
      <div
        role="note"
        // Kortet byts vid varje hover – skärmläsare ska få höra det nya
        aria-live="polite"
        aria-atomic="true"
        className={cn(
          'absolute bottom-12 right-[calc(100%-4px)] w-[230px] rounded-xl rounded-br-sm bg-surface border border-ink-200',
          'px-3 py-2 text-[12px] leading-snug text-ink-700 shadow-lg',
          'opacity-0 translate-y-1 pointer-events-none transition-all duration-200',
          'motion-reduce:transition-none motion-reduce:translate-y-0',
          'group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0',
        )}
      >
        {arElvis ? (
          <>
            <span className="block mb-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-brand-700">
              {elvisKort.kort.sort === 'tips' ? 'Tips' : 'Visste du?'}
            </span>
            {elvisKort.kort.text}
          </>
        ) : (
          <>
            <span className="font-semibold text-ink-900">{figur.namn}</span> – {bubbla}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={dolj}
        aria-label={`Dölj ${figur.namn}`}
        className={cn(
          'absolute -top-1 -right-1 z-10 w-6 h-6 grid place-items-center rounded-full bg-surface border border-ink-200 text-ink-500',
          'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity motion-reduce:transition-none hover:text-ink-900',
          // Pekskärm: ingen hover – knappen ska synas innan någon kan träffa den
          '[@media(hover:none)]:opacity-80',
        )}
      >
        <X size={12} strokeWidth={2.25} />
      </button>

      <div className="maskot-svavar">
        {/* Studsen vid hover ligger på ett eget lager, så att svävningen och
            skuggan aldrig startas om och kommer ur takt */}
        <div className="maskot-studs">
          {vem === 'elvis' ? (
            // ELvis i lager: blinkar, tittar och reagerar på chatten
            <div className="w-[88px] ml-auto mr-3 drop-shadow-sm">
              <MaskotElvis />
            </div>
          ) : (
            <img src={FIGURER.elvira.bild} alt="" width={112} height={112} draggable={false} className="block w-full h-auto drop-shadow-sm" />
          )}
        </div>
      </div>
      {/* Skuggan andas med svävningen */}
      <div className="maskot-skugga mx-auto -mt-1 h-2 w-14 rounded-[50%] bg-ink-900/15 blur-[3px]" aria-hidden />
    </div>
  );
}
